import { getIn, useFormikContext } from 'formik';

import { type Nullable, type TAutocompleteOption } from '~/common/types';
import Autocomplete from '../autocomplete/AutoComplete';

type Props = {
  freeSolo?: boolean;
  isRequired?: boolean;
  multiple?: boolean;
  name: string;
  label: React.ReactNode;
  onChangeCallback?: (value: any) => void;
  options: TAutocompleteOption<any>[];
  disabled?: boolean;
  [key: string]: unknown;
};

const FormikAutocomplete: React.FC<Props> = ({
  freeSolo = false,
  isRequired = false,
  label,
  multiple = false,
  name,
  onChangeCallback = () => {},
  options,
  disabled = false,
  ...rest
}) => {
  const { errors, setFieldTouched, setFieldValue, submitCount, touched, values } =
    useFormikContext();
  const showError = (getIn(touched, name) || submitCount > 0) && !!getIn(errors, name);

  const onChange = ({
    value,
  }: {
    value: Nullable<TAutocompleteOption<unknown>> | TAutocompleteOption<unknown>[];
  }) => {
    setFieldTouched(name, true);
    if (freeSolo) {
      setFieldValue(name, value);
      onChangeCallback(value);
      return;
    }

    if (multiple) {
      const vals = (value as TAutocompleteOption<unknown>[]).map((v) => v.value);
      setFieldValue(name, vals);
      onChangeCallback(vals);
      return;
    }
    const val = (value as Nullable<TAutocompleteOption<unknown>>)?.value;
    setFieldValue(name, val);
    onChangeCallback(val);
  };

  const getValue = () => {
    const currentValue = getIn(values, name);

    if (multiple && currentValue) {
      return options.filter((o) => currentValue.includes(o.value));
    }

    if (freeSolo) {
      return getIn(values, name);
    }

    return options.find(
      (o) => o.value === currentValue || (o.value?.key && o.value.key === currentValue?.key),
    );
  };

  const getError: (errorObj: string | object | null | undefined) => string | undefined = (
    errorObj,
  ) => {
    if (!errorObj) {
      return undefined;
    }
    if (typeof errorObj === 'string' || errorObj instanceof String) {
      return errorObj as string;
    }
    if (Object.values(errorObj).length) {
      return getError(Object.values(errorObj)[0]);
    }
    return undefined;
  };

  return (
    <Autocomplete
      {...rest}
      disabled={disabled}
      name={name}
      label={label}
      multiple={multiple}
      isRequired={isRequired}
      freeSolo={freeSolo}
      onBlur={() => setFieldTouched(name, true)}
      options={options}
      value={getValue() ?? null}
      error={showError}
      onChange={onChange}
      helperText={showError ? getError(getIn(errors, name)) : undefined}
    />
  );
};

export default FormikAutocomplete;
